import { useState } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { useLocation, Outlet, useNavigate, Link } from 'react-router-dom';
import { AppSidebar } from '@/components/app-sidebar';
import { useDashboard } from '@/contexts/dashboard-context';
import { formatSubjectName } from '@/utils/format';
import { Badge } from "@/components/ui/badge";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"

import { Separator } from "@/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"

export function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedSubject, isChatMode, setSelectedSubject, setIsChatMode } = useDashboard();

  // Dynamic breadcrumb based on current route and dashboard state
  const getBreadcrumbs = () => {
    const path = location.pathname;
    
    if (path === '/dashboard') {
      if (isChatMode && selectedSubject) {
        // Show subject name with Class 9 badge when in chat mode
        return {
          section: 'Dashboard',
          page: formatSubjectName(selectedSubject.name),
          showBadge: true
        };
      } else {
        // Show "Subject Selection" when not in chat mode
        return {
          section: 'Dashboard',
          page: 'Subject Selection'
        };
      }
    } else if (path.startsWith('/dashboard/chat/')) {
      // For chat URLs, check if we have a selected subject from dashboard context
      if (selectedSubject) {
        return {
          section: 'Dashboard',
          page: formatSubjectName(selectedSubject.name),
          showBadge: true
        };
      } else {
        // Return Subject Selection while loading, no "Chat" fallback
        return {
          section: 'Dashboard',
          page: 'Subject Selection'
        };
      }
    } else if (path === '/beacons') {
      return {
        section: 'Explore',
        page: 'Knowledge Beacons'
      };
    } else if (path.startsWith('/beacons/learn/')) {
      return {
        section: 'Learning',
        page: 'Beacon'
      };
    } else if (path === '/profile') {
      return {
        section: 'Settings',
        page: 'Profile'
      };
    }
    
    // Default breadcrumb
    return {
      section: 'Application',
      page: 'Home'
    };
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink asChild>
                    <Link to="/dashboard">
                      {breadcrumbs.section}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <div className="flex items-center gap-2">
                    {breadcrumbs.showBadge ? (
                      <button
                        onClick={() => {
                          setSelectedSubject(null);
                          setIsChatMode(false);
                          navigate('/dashboard');
                        }}
                        className="hover:text-foreground transition-colors cursor-pointer"
                      >
                        {breadcrumbs.page}
                      </button>
                    ) : (
                      <BreadcrumbPage>{breadcrumbs.page}</BreadcrumbPage>
                    )}
                    {breadcrumbs.showBadge && (
                      <Badge variant="secondary" className="text-xs">
                        Class 9
                      </Badge>
                    )}
                  </div>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
